"use server";

import { prisma } from "@/lib/prisma";
import { sendEmail, corporateQuoteEmail } from "@/lib/notify";

export type LeadState = { ok?: true; error?: string } | undefined;

// Where Corporate quote requests are routed for follow-up.
const QUOTE_NOTIFY_EMAIL = "itsharshag@gmail.com";

function cleanEmail(v: FormDataEntryValue | null) {
  return String(v ?? "").trim().toLowerCase();
}

// /apply — founding-access application. Captured as a Waitlist lead.
export async function applyAction(
  _prev: LeadState,
  formData: FormData,
): Promise<LeadState> {
  const email = cleanEmail(formData.get("email"));
  const name = String(formData.get("full_name") ?? "").trim();
  const business = String(formData.get("business_name") ?? "").trim();
  if (!email || !email.includes("@")) return { error: "Enter a valid email." };
  if (!name) return { error: "Enter your name." };
  if (!business) return { error: "Enter your business name." };
  const budget = String(formData.get("budget") ?? "").trim() || null;
  const categories = String(formData.get("categories") ?? "").trim() || null;
  const channels = String(formData.get("sales_channels") ?? "").trim() || null;
  const message = String(formData.get("message") ?? "").trim() || null;
  const data = { name, business, budget, categories, channels, message };
  try {
    await prisma.waitlist.upsert({
      where: { email },
      create: { email, ...data },
      update: data,
    });
  } catch {
    return { error: "Something went wrong. Please try again." };
  }
  return { ok: true };
}

// Floating "Talk to us" widget: a phone-number callback request. Stored as a
// ContactSubmission so it shows up alongside every other inbound message in the
// admin console. No email is collected, so the email column stays blank.
export async function callbackAction(
  _prev: LeadState,
  formData: FormData,
): Promise<LeadState> {
  const phone = String(formData.get("phone") ?? "").trim();
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 7) return { error: "Enter a valid phone number." };
  try {
    await prisma.contactSubmission.create({
      data: {
        name: "Callback request",
        email: "",
        subject: "Phone callback",
        message: `Requested a callback at ${phone}`,
      },
    });
  } catch {
    return { error: "Something went wrong. Please try again." };
  }
  return { ok: true };
}

// /sell: "Sell With Us" pitch. Captured as a SellSubmission (full log, no
// dedup) so the team sees every seller inquiry in the admin console.
export async function sellAction(
  _prev: LeadState,
  formData: FormData,
): Promise<LeadState> {
  const name = String(formData.get("name") ?? "").trim();
  const email = cleanEmail(formData.get("email"));
  const phone = String(formData.get("phone") ?? "").trim() || null;
  const company = String(formData.get("company") ?? "").trim() || null;
  const product = String(formData.get("product") ?? "").trim();
  const quantity = String(formData.get("quantity") ?? "").trim() || null;
  const price = String(formData.get("price") ?? "").trim() || null;
  const description = String(formData.get("description") ?? "").trim();
  if (!name) return { error: "Enter your name." };
  if (!email || !email.includes("@")) return { error: "Enter a valid email." };
  if (!product) return { error: "Tell us what you want to sell." };
  if (!description) return { error: "Add a short description." };
  try {
    await prisma.sellSubmission.create({
      data: { name, email, phone, company, product, quantity, price, description },
    });
  } catch {
    return { error: "Something went wrong. Please try again." };
  }
  return { ok: true };
}

// Corporate plan "Request a Quote". The Corporate tier is not self-serve
// checkoutable: the buyer customizes their spending power, leaves an email +
// phone, and we log the request and ping the ops inbox to follow up.
export async function corporateQuoteAction(
  _prev: LeadState,
  formData: FormData,
): Promise<LeadState> {
  const email = cleanEmail(formData.get("email"));
  const phone = String(formData.get("phone") ?? "").trim();
  const powerCents = Math.round(Number(formData.get("power") ?? 0));
  if (!email || !email.includes("@")) return { error: "Enter a valid email." };
  if (phone.replace(/\D/g, "").length < 7)
    return { error: "Enter a valid phone number." };
  const spendingPowerCents =
    Number.isFinite(powerCents) && powerCents > 0 ? powerCents : 0;
  try {
    await prisma.corporateQuoteRequest.create({
      data: { email, phone, spendingPowerCents },
    });
  } catch {
    return { error: "Something went wrong. Please try again." };
  }
  // Best-effort notification: never fail the request if email is down.
  try {
    await sendEmail(
      QUOTE_NOTIFY_EMAIL,
      "New Corporate quote request",
      corporateQuoteEmail({ email, phone, spendingPowerCents }),
    );
  } catch {
    // swallow: the row is already saved and visible in the admin console.
  }
  return { ok: true };
}

// /contact: general "Get in Touch" inquiry. Logged as a ContactSubmission so
// the admin console sees every message (with its subject) in full.
export async function contactAction(
  _prev: LeadState,
  formData: FormData,
): Promise<LeadState> {
  const email = cleanEmail(formData.get("email"));
  const name = String(formData.get("name") ?? "").trim();
  const subject = String(formData.get("subject") ?? "").trim() || null;
  const message = String(formData.get("message") ?? "").trim();
  if (!name) return { error: "Enter your name." };
  if (!email || !email.includes("@")) return { error: "Enter a valid email." };
  if (!message) return { error: "Enter a message." };
  try {
    await prisma.contactSubmission.create({
      data: { name, email, subject, message },
    });
  } catch {
    return { error: "Something went wrong. Please try again." };
  }
  return { ok: true };
}
